import type {
  ConsentSectionConfiguration,
  DeviceRegistrationConfiguration,
  OtpChannel,
  PurposeGrouping,
} from "../types";

export type DynamicLink = {
  linkDestination: string;
  linkDisplayValue: string;
  placeholder: string;
};

export type AgeVerificationScriptConfigItem = {
  code: string;
  scriptKey: string;
  scriptUrl: string;
  workflowDefinitionId: string;
  workflowName: string;
};

export type FirebaseSdkCredentials = Partial<{
  firebaseApiKey: string;
  firebaseAuthDomain: string;
  firebaseProjectId: string;
  firebaseStorageBucket: string;
  firebaseMessagingSenderId: string;
  firebaseAppId: string;
}>;

export type WebPushAutoPromptConfig = {
  configurationVersion?: string;
  cooldownDays?: number;
  maxImpressions?: number;
  includedPagePatterns?: string[];
  excludedPagePatterns?: string[];
};

export type FeedbackTranslations = {
  wasItHelpful?: string;
  helpful?: string;
  notHelpful?: string;
  thanksForFeedback?: string;
  feedbackSubmitted?: string;
  limitReached?: string;
  justLimitReached?: string;
  feedbackMechanismReward?: string;
  loginText?: string;
  loginLabel?: string;
  signUpLabel?: string;
};

export type ColorVariant = {
  color?: string;
  image?: string;
};

export type ProductSpecification = {
  specification?: string;
}[];

export type Language = {
  current: boolean;
  path: string;
  associatedCookieValue: string;
  locale?: string;
  languageDisplayName: string;
};

export type LanguageSelectorVariant = "FULL_LANGUAGE_NAME" | "SHORT_LANGUAGE_NAME";

export type LanguageSelectorVariantConfig = {
  mobile?: LanguageSelectorVariant;
  desktop?: LanguageSelectorVariant;
};
export type GoogleLoginMode = "popup" | "redirect";

export type MapProvider = "google" | "naver" | "yandex";

export type KsaProvince = {
  name: string;
  cities: string[];
};

export type AddressFormVariant =
  | "standard"
  | "standard_reversed"
  | "single_field_lookup"
  | "extended_asian"
  | "extended_asian_reversed"
  | "united_arab_emirates"
  | "saudi_arabia"
  | "jordan"
  | "with_regions"
  | "with_regions_reversed"
  | "taiwan"
  | "extended_with_province"
  | "extended_with_province_reversed";
export type LocationServiceAutocompleteService = "google" | "daum" | "yandex" | "none";

export type DobFormType =
  | "inputs"
  | "inputs_reversed"
  | "dropdowns_filled"
  | "dropdowns_filled_reversed"
  | "dropdowns_empty"
  | "dropdowns_empty_reversed";

export type LoyaltyCardType = "qr_code" | "bar_code";

export type PostCodeKeyboardType = "default" | "numeric";

export type WalletButtonsVisibility = "none" | "all" | "apple" | "google";

export type TaxesDisplayMode = "SimpleTotalBreakdown" | "BreakdownWithNames" | "Both";

export type FeatureFlags = {
  ffCheckoutV2EnableIteration2?: boolean;
  ffCheckoutV2EnableIteration3?: boolean;
  ffFreeShippingProgressBar?: boolean;
  ffNewMiniCartEnable?: boolean;
};

export type Theme = "light" | "dark";

export type WithdrawalOrderConfiguration = {
  withdrawFromContractBtn?: string;
  withdrawalModalTitle?: string;
  withdrawalModalContent?: string;
  withdrawalModalConfirmBtn?: string;
  withdrawalModalCancelBtn?: string;
  withdrawalSuccessModalTitle?: string;
  withdrawalSuccessModalContent?: string;
  withdrawalSuccessLinkLabel?: string;
  withdrawalSuccessLinkUrl?: string;
  withdrawalRequestError?: string;
  withdrawalRequestErrorTitle?: string;
  withdrawalErrorLinkLabel?: string;
  withdrawalErrorLinkUrl?: string;
  withdrawalRequestSentText?: string;
  withdrawalOpenBannerTitle?: string;
  withdrawalOpenBannerDescription?: string;
  withdrawalClosedBannerTitle?: string;
  withdrawalClosedBannerDescription?: string;
  withdrawalOpenBannerLinkLabel?: string;
  withdrawalOpenBannerLinkUrl?: string;
  withdrawalClosedBannerLinkLabel?: string;
  withdrawalClosedBannerLinkUrl?: string;
  withdrawalRequestClosedText?: string;
  withdrawalTooltipMessage?: string;
  withdrawalEligibleTooltipMessage?: string;
  withdrawalForm?: {
    firstNameLabel?: string;
    orderLabel?: string;
    lastNameLabel?: string;
    emailLabel?: string;
    description?: string;
    helperText?: string;
    phoneLabel?: string;
    confirmButtonLabel?: string;
    title?: string;
    cancelButtonLabel?: string;
    phoneHelperText?: string;
    itemSingularLabel?: string;
    itemPluralLabel?: string;
  };
  withdrawalSuccessContent?: {
    successOrderLabel?: string;
    successFirstNameLabel?: string;
    successSubmittedOnLabel?: string;
    successLastNameLabel?: string;
    successPhoneLabel?: string;
    subtitle?: string;
    title?: string;
    successEmailLabel?: string;
    content?: string;
    successHelperText?: string;
  };
  withdrawalMessagePayload?: {
    product?: string;
    market: string;
  };
};

export type RegionSelectorOption = {
  id: string;
  regionName: string;
  isDefault: boolean;
};

export type Config = {
  EVO_TENANT: string;
  EVO_DOMAIN: string;
  EVO_VERSION: "v5" | "v6";
  USE_EVO_ONLY: boolean;
  "base-url": string;
  "default-store-view": string;
  "trials-store-view": string;
  "X-RECAPTCHA-FEATURE-TOGGLE": string;
  "X-RECAPTCHA-ID": string;
  AGE_VERIFICATION_ASYNC_SCRIPTS_CONFIG?: AgeVerificationScriptConfigItem[];
  AGE_VERIFICATION_PAGE: string;
  AUTH_PAGE_URL: string;
  AUTH_REGISTER_SUCCESS_PAGE_URL: string;
  AV_PHOTO: string;
  AVP_URL: string;
  OLA_URL: string;
  AVP_X_DES_AK_TENANT_TOKEN: string;
  OLA_X_DES_AK_TENANT_TOKEN: string;
  blockVideoDownload: boolean;
  CHECKOUT_PAGE_URL: string;
  CART_CHECKOUT_PAGE_URL: string;
  CAS_URL: string;
  CAS_X_DES_AK_TENANT_TOKEN: string;
  CHECKOUT_PHONE_REQUIRED: string;
  CONSENT_PROMO_EMAIL_PAGES: string;
  CONSENT_PROMO_SMS_PAGES: string;
  CONSENT_RENEWAL: string;
  CONTACT_PHONE: string;
  EMAIL_PATTERN?: string;
  CONTACT_US_URL: string;
  COUNTRY_NAME: string;
  currencyFormat?: string;
  customCurrencySymbol?: Record<string, string>;
  dateFormat?: string;
  DES_DEVICE_SERVICE_URL: string;
  GET_LOCATION_BY_IP_URL: string;
  DEVICE_REGISTRATION_CODE_IMG: string;
  DIS_DES_AK_TENANT_TOKEN: string;
  displayAvailableInStoresBundleOptions: boolean;
  displayAvailableInStoresStickProducts: boolean;
  displayAvailableInStoresVariants: boolean;
  displayOutOfStockBundleOptions: boolean;
  displayOutOfStockStickProducts: boolean;
  displayOutOfStockVariants: boolean;
  drawerFlavourSelectorForBundles: boolean;
  showDetailedSummaryOnThankYouPage: boolean;
  showFlavourAttributesInFlavourSelector: boolean;
  showFlavourDescriptionInFlavourSelector: boolean;
  showQuizOnThankYouPage: boolean;
  showNewThankYouPage: boolean;
  tradeInOnlyForRegistered: boolean;
  displayBundleSelectedItems: boolean;
  displayBundleSelectedItemsPrices: boolean;
  EES_URL: string;
  EES_X_DES_AK_TENANT_TOKEN: string;
  FAQ_PAGE: string;
  FLOTIQ_API_KEY: string;
  FLOTIQ_API_URL: string;
  FREE_TRIAL_FEATURE_FLAG: string;
  BRAND_CONSUMABLES_FEATURE_FLAG?: boolean;
  FREE_TRIAL_PAGE_URL: string;
  FREE_TRIAL_USER_DASHBOARD_SHOW: string;
  freeGiftsPurposes?: string;
  GOOGLE_API_KEY: string;
  GOOGLE_MAPS_API: string;
  GOOGLE_PLACES_KEY: string;
  GTM_CODE: string;
  GTM_DOMAIN?: string;
  HELP_CENTER_URL: string;
  HIDE_POST_CODE_INPUT: string;
  HOME_PAGE_URL: string;
  IRS_URL: string;
  IRS_X_DES_AK_TENANT_TOKEN: string;
  IS_LOYALTY_ENABLED: string; // Configured on builder level
  isLoyaltyActive?: boolean; // BE status fetched from tenant config (Evo public domain)
  isReferralOn?: boolean;
  isAgeVerifiedLoyaltyRestriction: boolean;
  isLoyaltyContentRestricted: boolean;
  isDeviceRegisteredRestriction: boolean;
  ISO_COUNTRY_CODE: string;
  LOGIN_PAGE_URL: string;
  LOGOUT_PAGE_URL: string;
  LOQATE_API_KEY: string;
  LOYALTY_KNOW_MORE_URL: string;
  LOYALTY_PROFILE_PAGE_URL: string;
  LOYALTY_REFERRALS_PAGE_URL: string;
  LOYALTY_PRIZE_PAGE_URL: string;
  IS_LOYALTY_REFERRALS_ENABLED: string;
  LOYALTY_REQUEST_TIMEOUT: string;
  LOYALTY_TOKEN: string;
  LOYALTY_URL: string;
  loyaltyRestrictionWithoutRedirectionSlugUrls: string[];
  loyaltyRestrictionPurpose: string[];
  MAGENTO_SERVICE_URL: string;
  mapProviderExternalJs?: string;
  MIN_AGE_TO_REGISTER: string;
  NEW_RELIC_NOTICE_ERROR: string;
  OMP_URL: string;
  ONE_TRUST_DYNAMIC_LINKS?: DynamicLink[];
  availableInStoresBundleOptionsNotification: string;
  outOfStockBundleOptionsNotification: string;
  PGS_URL: string;
  PGS_X_DES_AK_TENANT_TOKEN: string;
  PHONE_FORMAT: string;
  PHONE_NUMBER_MAX_LENGTH: string;
  PHONE_NUMBER_MIN_LENGTH: string;
  PHONE_LOGIN_OTP_TIMEOUT: string;
  PHONE_LOGIN_OTP_NUMBER_OF_ATTEMPTS: string;
  PLOOMX_DEVICES_FOLDER: string;
  PREFERRED_FREQUENCY_DEFAULT: string;
  PREFERRED_FREQUENCY_LIST: string;
  PREFERRED_LANG_DEFAULT: string;
  PREFERRED_LANG_LIST: string;
  PRIVACY_PAGE_URL: string;
  PROFILE_PAGE_URL: string;
  PROFILE_REFERRAL_BACKGROUND_MOBILE: string;
  PROFILE_REFERRAL_BACKGROUND: string;
  PVS_URL: string;
  PVS_X_DES_AK_TENANT_TOKEN: string;
  REDIRECT_AFTER_LOGIN_URL: string;
  REDIRECT_TO_DEVICE_REGISTRATION_ON_PROFILE: string;
  REFERRAL_TOKEN: string;
  REFERRAL_URL: string;
  REGISTER_PHONE_REQUIRED: string;
  REGISTRATION_AGE_VERIFICATION_ENABLED: string;
  REGISTRATION_AGE_VERIFICATION_OPTIONAL: string;
  REGISTRATION_DEVICE_ENABLED: string;
  REGISTRATION_EMAIL_OTP_NUMBER_OF_ATTEMPTS: string;
  REGISTRATION_EMAIL_OTP_TIMER: string;
  REGISTRATION_EMAIL_VERIFICATION_ENABLED: string;
  REGISTRATION_PHONE_VERIFICATION_ENABLED: string;
  REGISTRATION_SMS_OTP_COUNTER_ENABLED: string;
  REGISTRATION_SMS_OTP_NUMBER_OF_ATTEMPTS: string;
  REGISTRATION_SMS_OTP_TIMER: string;
  REGISTRATION_WIZARD: string;
  RESET_PAGE_URL: string;
  SHOP_URL: string;
  PDP_URL?: string;
  showVideoControls: boolean;
  isEmailEditable: boolean;
  isPhoneEditable: boolean;
  SNP_URL: string;
  SNP_X_DES_AK_TENANT_TOKEN: string;
  STORE_LOCATOR_CONTENT_TYPE: string;
  STREET_ADDRESS_REVERSE_ORDER: string;
  TELEPHONE_CODES_LIST: string;
  TERMS_ADULT_AGE: string;
  TERMS_PAGE_URL: string;
  TRANSLATION_TOOL: string;
  TSS_TOKEN: string;
  TSS_URL: string;
  UPDATE_PHONE_AVP: string;
  UPDATE_PHONE_OTP: string;
  X_DES_AK_TENANT_TOKEN_CONTACT: string;
  X_DES_AK_TENANT_TOKEN?: string;
  GOOGLE_SEARCH_CONSOLE_VERIFICATION_TAG?: string;
  allowInStoreDelivery: boolean;
  allowPayInStoreOnDelivery: boolean;
  allowSeparateCartsForStores: boolean;
  enableSubscriptions: boolean;
  languages?: Language[];
  isInternationalPhoneEnabled: boolean;
  hideInternationalPhoneDisclaimer: boolean;
  internationalPhoneCountriesInDropdown: string[];
  PLATFORM_CONFIGURATION_URL: string;
  isBillingAddressVisibleForCheckout: boolean;
  isMobileFullscreenLanguageSelectorEnabled: boolean;
  LOYALTY_CARD_TYPE: LoyaltyCardType;
  ANDROID_IMG: string;
  APPLE_IMG: string;
  isLoyaltyContestsServiceEnabled: boolean;
  isTokenAuthEnabled: boolean;
  tokenAuthPageTitle?: string;
  tokenAuthErrorTitle?: string;
  tokenAuthErrorDescription?: string;
  tokenAuthLoadingTitle?: string;
  tokenAuthLoadingDescription?: string;
  tokenAuthMissingTokenTitle?: string;
  tokenAuthMissingTokenDescription?: string;
  tokenAuthBannerTitle?: string;
  tokenAuthBannerDescription?: string;
  tokenAuthBannerLinkLabel?: string;
  tokenAuthBannerLinkHref?: string;
  storeLocatorMapTheme: Theme;
  regionSelector: {
    isRegionSelectorEnabled: boolean;
    regionOptions: RegionSelectorOption[];
  };

  // Mobile first navigation - to be removed after all markets will prepare content for migration
  isMobileFirstNavigationEnabled?: boolean;

  // Commerce Settings
  DISABLE_COMMERCE?: boolean;
  hideTeaserConfigurableOptions?: boolean;
  defaultProductCtaLabel?: string;
  defaultProductCtaUrl?: string;
  isTradeInAvailableWithoutLogin?: boolean;
  exploreProductsUrlForTradeInAvailableWithoutLogin?: string;
  isTradeInEligibilityBannerEnabled?: boolean;
  tradeInEligibilityBannerTitle?: string;
  tradeInEligibilityBannerDescription?: string;
  isQuickAddToCartCtaForSimpleSticksEnabled?: boolean;
  isCancelOrderCTAEnabled?: boolean;
  isConsumablesMatrixEnabled?: boolean;
  restrictedSkusForPayOnDelivery?: string;
  showPricesWithTaxExcluded?: boolean;
  paymentMerchantName?: string;

  // LiveChat
  liveChatLazyLoading: boolean;

  // TrustPilot
  isEnabledForTenant: boolean;
  scriptPath: string;
  leaveAReviewLink: string;
  localeCode: string;
  businessUnitId: string;
  microComboWidgetTemplateId: string;
  microReviewCountWidgetTemplateId: string;
  reviewsWidgetTemplateId: string;
  starsWidgetTemplateId: string;
  theme: string;

  // Recaptcha
  reCaptchaPrivacyPolicyUrl: string;
  reCaptchaTermsOfServiceUrl: string;
  reCaptchaEnabled: boolean;
  brandingInformationEnabled: boolean;

  // DES EVO Authentication
  defaultDesEvoLoginStrategy: string;

  // Kakao
  isKakaoLoginEnabled: boolean;
  isLoginThroughKakaoTalkEnabled: boolean;
  kakaoButtonTitle: string;
  kakaoIntegrityKey: string;
  kakaoRedirectUrlAfterLogin: string;
  kakaoSdkKey: string;
  kakaoSdkLink: string;

  // PASS age verification
  PORTONE_STORE_ID: string;
  PORTONE_CHANNEL_KEY: string;

  // Location Service
  locationServiceMapProvider: MapProvider;
  locationServiceAutocompleteProvider: LocationServiceAutocompleteService;
  addressFormVariant: AddressFormVariant;
  autocompleteProviderApiUrl: string;
  autocompleteProviderExternalJs: string;
  googleMapsCityComponentType: string; // coma-separated component types (locality,sublocality_level_1)
  isRegionFieldOptional: boolean;
  isSingleFieldAddressLookup: boolean;
  storeLocatorLayoutVersion: "v1" | "v2";
  isStreetFieldCombined: boolean;
  ksaProvinces: KsaProvince[];

  // Promo Code Configuration
  hidePromoCodeBlockOnCheckoutLandingPage: boolean;
  hidePromoCodeBlockOnCheckoutShippingStep: boolean;
  hidePromoCodeBlockOnCheckoutDeliveryStep: boolean;

  // Domain whitelisting
  whitelistedDomains: string[];
  enableDomainWhitelist?: boolean;

  // KOR market single field for user name
  isSingleNameField: boolean;

  // Reversed name order (Family Name before Given Name)
  isReversedNameOrder: boolean;

  SAV_BYPASS_TOKEN: string;

  wordBreak: string;

  // DOB settings
  dobFormType: DobFormType;

  // OTP channels
  isOtpSelectionEnabled: boolean;
  defaultOtpChannel: OtpChannel;
  otpOptions: {
    whatsapp: boolean;
    viber: boolean;
  };

  // Options to disable parts of Dashboard
  DISABLE_USER_DASHBOARD_DEVICES: boolean;
  DISABLE_USER_DASHBOARD_FREE_TRIAL: boolean;

  // Legal consents text
  consentsLegalText?: string;

  // Firebase config
  isFirebaseEnabled?: boolean;
  firebaseVapidKey?: string;
  firebaseSdkCredentials?: FirebaseSdkCredentials;
  webPushLearnMoreSuccessLink?: string;
  webPushLearnMoreDeclineLink?: string;
  webPushConsentId?: string;
  isWebPushAutoPromptEnabled?: boolean;
  webPushAutoPromptConfig?: WebPushAutoPromptConfig;

  // Product Detail Page Configuration
  sortByProductAttribute?: boolean;
  productSpecification?: ProductSpecification;

  // Session ttl (keep me logged in)
  overrideLoginSessionTtl?: string;

  // X-Selling Pop-up
  isXSellingPopupEnabled?: boolean;

  // Withdrawal Feature
  isWithdrawalFeatureEnabled?: boolean;
  isWithdrawalFormForLoggedInEnabled?: boolean;
  isWithdrawalFormForNotLoggedInEnabled?: boolean;

  // Checkout V2
  isCheckoutV2Enabled?: boolean;
  consentReminderMode?: "global" | "legacy" | "disabled";

  // Device Image Transformation
  deviceImages?: {
    ploomx?: ColorVariant[];
    plooms?: ColorVariant[];
    ploomgen4?: ColorVariant[];
    ploomgen4aura?: ColorVariant[];
    ploomgen4cube?: ColorVariant[];
  };
  // Post code keyboard type
  postCodeKeyboardType?: PostCodeKeyboardType;
  languageSelectorVariant?: {
    header?: {
      variant?: LanguageSelectorVariantConfig;
    };
    softAgeVerification?: {
      variant?: LanguageSelectorVariantConfig;
    };
  };

  // Google login
  isGoogleLoginEnabled?: boolean;
  googleLoginClientLink?: string;
  googleLoginClientId?: string;
  googleLoginScopes?: string;
  googleLoginMode?: GoogleLoginMode;

  // Facebook login
  isFacebookLoginEnabled?: boolean;
  facebookAppId?: string;
  facebookScriptVersion?: string;
  facebookLoginScopes?: string;

  smsOtpCustomTemplateNames?: {
    registrationOtpTemplateName?: string;
    phoneLoginOtpTemplateName?: string;
    phoneUpdateOtpTemplateName?: string;
    registration?: Map<OtpChannel, string>;
    phoneLogin?: Map<OtpChannel, string>;
    phoneUpdate?: Map<OtpChannel, string>;
  };

  // Custom preference center
  isCpcPhoneEditable?: boolean;
  isCpcEmailEditable?: boolean;
  isCpcInternationalPhoneEnabled?: boolean;
  isCpcPhoneRequired?: boolean;
  isCpcPhoneOtpEnabled?: boolean;
  isCpcPhoneAvEnabled?: boolean;
  isCpcAcceptAllButtonEnabled?: boolean;
  isCpcOtpSelectionEnabled?: boolean;
  isCpcLanguagePreferenceHidden?: boolean;
  isCpcOptionalConsentNotRequired?: boolean;
  cpcSmsOtpTimer?: string | null;
  cpcPhoneUpdateOtpTemplateName?: string | null;
  cpcSmsNumberOfAttempts?: string | null;
  cpcPhoneCodesList?: string | null;
  cpcDefaultOtpChannel?: OtpChannel;
  cpcPurposesGroupingType?: PurposeGrouping;
  cpcConsentOptionsListType?: "default" | "extended";
  cpcConsentsLegalText?: string | null;
  cpcConsentsSuccessUpdateMessage?: string | null;
  cpcOneTrustErrorMessage?: string | null;
  cpcOneTrustErrorRedirectUrl?: string | null;
  cpcOneTrustErrorLinkText?: string | null;
  cpcProfileLink?: string | null;
  cpcConsentsPageLink?: string | null;
  cpcErrorText?: string | null;
  cpcErrorButtonText?: string;
  cpcErrorButtonLink?: string;

  // E-wallet
  walletButtonsVisibility?: WalletButtonsVisibility;

  // Withdrawal Order Configuration
  withdrawalOrderConfig?: WithdrawalOrderConfiguration | null;

  // PDP Consent Card Configuration
  pdpConsentCard?: ConsentSectionConfiguration | null;

  // Device Registration Configuration
  deviceRegistrationConfiguration?: DeviceRegistrationConfiguration;

  // Hidden Age Verification
  asyncHiddenAvName?: string;
  asyncHiddenAvTimeout?: number;
  isPersonalInformationRequiredForAsyncAv?: boolean;

  // Taxes
  taxesDisplayMode?: TaxesDisplayMode;

  // Feature Flags
  featureFlags: FeatureFlags;

  // SEO
  isSeoFriendlyStoreLocatorEnabled?: boolean;

  // Feedback mechanism
  isFeedbackMechanismEnabled?: boolean;
  feedbackTranslations?: FeedbackTranslations;
  feedbackSignUpUrl?: string;
};

export type AuthConfig = Pick<
  Config,
  // Facebook login
  | "isFacebookLoginEnabled"
  | "facebookAppId"
  | "facebookScriptVersion"
  | "facebookLoginScopes"
  // Google login
  | "isGoogleLoginEnabled"
  | "googleLoginClientLink"
  | "googleLoginClientId"
  | "googleLoginScopes"
  | "googleLoginMode"
  // Kakao login
  | "isKakaoLoginEnabled"
  | "isLoginThroughKakaoTalkEnabled"
  | "kakaoSdkLink"
  | "kakaoIntegrityKey"
  | "kakaoSdkKey"
  | "kakaoButtonTitle"
  | "kakaoRedirectUrlAfterLogin"
  // Hidden age verification
  | "asyncHiddenAvName"
  | "asyncHiddenAvTimeout"
  | "isPersonalInformationRequiredForAsyncAv"
  // Registration settings
  | "REGISTRATION_PHONE_VERIFICATION_ENABLED"
  | "REGISTRATION_DEVICE_ENABLED"
  | "REGISTRATION_AGE_VERIFICATION_ENABLED"
  | "REGISTRATION_AGE_VERIFICATION_OPTIONAL"
  | "REGISTRATION_EMAIL_VERIFICATION_ENABLED"
  | "REGISTRATION_EMAIL_OTP_TIMER"
  | "REGISTRATION_EMAIL_OTP_NUMBER_OF_ATTEMPTS"
  | "REGISTRATION_SMS_OTP_COUNTER_ENABLED"
  | "PHONE_LOGIN_OTP_TIMEOUT"
  | "PHONE_LOGIN_OTP_NUMBER_OF_ATTEMPTS"
  | "EMAIL_PATTERN"
  | "CONSENT_RENEWAL"
  | "REDIRECT_TO_DEVICE_REGISTRATION_ON_PROFILE"
>;

export type ProfileConfig = Pick<
  Config,
  | "isCpcPhoneEditable"
  | "isCpcEmailEditable"
  | "isCpcInternationalPhoneEnabled"
  | "isCpcPhoneRequired"
  | "isCpcPhoneOtpEnabled"
  | "isCpcPhoneAvEnabled"
  | "isCpcAcceptAllButtonEnabled"
  | "isCpcOtpSelectionEnabled"
  | "isCpcLanguagePreferenceHidden"
  | "isCpcOptionalConsentNotRequired"
  | "cpcSmsOtpTimer"
  | "cpcPhoneUpdateOtpTemplateName"
  | "cpcSmsNumberOfAttempts"
  | "cpcPhoneCodesList"
  | "cpcDefaultOtpChannel"
  | "cpcPurposesGroupingType"
  | "cpcConsentOptionsListType"
  | "cpcConsentsLegalText"
  | "cpcConsentsSuccessUpdateMessage"
  | "cpcOneTrustErrorMessage"
  | "cpcOneTrustErrorRedirectUrl"
  | "cpcOneTrustErrorLinkText"
  | "cpcProfileLink"
  | "cpcConsentsPageLink"
  | "cpcErrorText"
  | "cpcErrorButtonText"
  | "cpcErrorButtonLink"
>;
